import { useId, useMemo, useState } from 'react';
import { Badge, Button, Checkbox, Field, Input, Modal, Select, Textarea } from '../../../components/ui';
import { formatCount } from '../../../lib/format';
import { toDateOnly } from '../../../lib/period';
import type { CompanySummary } from '../../../types';
import { VARIAVEIS_DO_EMAIL, formularioDeCampanha, formularioEmailInicial, pendenciasDaCampanha, type FormularioCampanhaEmail } from '../campanhaEmail';
import { LIMITE_PREHEADER, checarEntrega } from '../entrega';
import type { EmailCampaign, EmailDomainHealth, SegmentKey } from '../types';
import { useFeedbackTemporario } from '../useFeedbackTemporario';
import { SEGMENT_KEYS, SEGMENT_LABEL, isSegmentKey } from '../vocabulario';
import { ChecklistDeEntrega } from './ChecklistDeEntrega';
import { MedidorDeCaracteres } from './MedidorDeCaracteres';
import { PreviaEmailDispositivo } from './PreviaEmailDispositivo';

/**
 * Compositor de campanha de e-mail (criar e editar), com previa ao vivo no
 * computador ou no celular e o teste de entrega enquanto escreve.
 *
 * Decisoes nao obvias:
 *  1. O estado do formulario nasce dentro do modal a partir de `inicial`;
 *     quem abre monta o componente com uma `key` nova a cada abertura para o
 *     rascunho anterior nao vazar para a proxima campanha.
 *  2. Assunto com medidor (celular corta em 40, computador em 60) e
 *     preheader com contador: e o que decide a abertura.
 *  3. O checklist de entrega mostra so o que precisa de atencao (modo
 *     compacto) e recalcula a cada tecla, com o dominio da empresa escolhida.
 *  4. Publico so pos-fato (mesmos segmentos do WhatsApp). "Disparar" vira
 *     "Agendar" quando a caixa de agendamento esta marcada: e a mesma acao
 *     com outro instante, nao um quarto botao.
 */

export function ModalNovaCampanhaEmail({
  open,
  onClose,
  companies,
  companyIdPadrao,
  inicial,
  domains,
  destinatarios,
  onSalvarRascunho,
  onDisparar,
}: {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly companies: readonly CompanySummary[];
  readonly companyIdPadrao: string;
  /** Campanha em edicao; `null` para nova. */
  readonly inicial: EmailCampaign | null;
  readonly domains: readonly EmailDomainHealth[];
  /** Destinatarios estimados do publico na empresa escolhida. */
  readonly destinatarios: (companyId: string, segmentKey: SegmentKey) => number;
  readonly onSalvarRascunho: (form: FormularioCampanhaEmail) => void;
  readonly onDisparar: (form: FormularioCampanhaEmail) => void;
}): JSX.Element | null {
  const medidorId = useId();
  const amanha = useMemo(() => toDateOnly(new Date(Date.now() + 86_400_000)), []);
  const [form, setForm] = useState<FormularioCampanhaEmail>(() => (inicial === null ? formularioEmailInicial(companyIdPadrao, amanha) : formularioDeCampanha(inicial, amanha)));
  const { mensagem, mostrar } = useFeedbackTemporario();
  const alterar = (patch: Partial<FormularioCampanhaEmail>): void => setForm((prev) => ({ ...prev, ...patch }));

  const publico = destinatarios(form.companyId, form.segmentKey);
  // Publico vazio (expirados sem o funil, empresa sem historico) nao dispara: sairia uma campanha 0/0.
  const pendencias = [...pendenciasDaCampanha(form), ...(publico <= 0 ? ['escolha um público com destinatários nesta empresa'] : [])];
  const completo = pendencias.length === 0;
  const empresa = companies.find((c) => c.id === form.companyId) ?? null;
  const dominio = domains.find((d) => d.companyId === form.companyId) ?? null;
  const remetente = empresa === null ? 'Sua empresa' : `${empresa.name} <contato@${dominio?.domain ?? `${empresa.slug}.exemplo`}>`;
  const editando = inicial !== null;
  const entrega = checarEntrega(form, dominio);
  const preheaderTamanho = Array.from(form.preheader).length;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={editando ? 'Editar campanha' : 'Nova campanha de e-mail'}
      description="Escreva, escolha o público pós-fato e dispare. Variáveis entre chaves são trocadas por contato."
      footer={
        <>
          {mensagem === null ? null : (
            <span role="status" className="mr-auto flex flex-wrap items-center gap-2 text-sm text-ink">
              <Badge tone="warning" dot>
                Simulado na prévia
              </Badge>
              {mensagem}
            </span>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="secondary" icon="modelo" onClick={() => onSalvarRascunho(form)} disabled={form.subject.trim() === ''}>
            Salvar rascunho
          </Button>
          <Button variant="secondary" icon="email" onClick={() => mostrar('Teste registrado para a equipe — nada foi enviado.')} disabled={!completo}>
            Enviar teste
          </Button>
          <Button variant="primary" icon={form.agendar ? 'calendario' : 'disparo'} onClick={() => onDisparar(form)} disabled={!completo}>
            {form.agendar ? 'Agendar' : 'Disparar'}
          </Button>
        </>
      }
    >
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <Field label="Empresa" required hint={dominio === null ? 'Em nome de quem o e-mail sai.' : `Sai pelo domínio ${dominio.domain}.`}>
            {({ id, describedBy }) => (
              <Select id={id} aria-describedby={describedBy} value={form.companyId} onChange={(e) => alterar({ companyId: e.target.value })} disabled={editando}>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Assunto" required htmlFor={`${medidorId}-assunto`}>
            {({ id }) => (
              <div className="flex flex-col gap-1.5">
                <Input
                  id={id}
                  aria-describedby={medidorId}
                  value={form.subject}
                  maxLength={120}
                  placeholder="Ex.: {primeiro_nome}, seu {produto} ficou parado no checkout"
                  onChange={(e) => alterar({ subject: e.target.value })}
                />
                <MedidorDeCaracteres id={medidorId} texto={form.subject} />
              </div>
            )}
          </Field>
          <Field
            label="Preheader"
            hint={
              <span className={preheaderTamanho > LIMITE_PREHEADER ? 'text-status-warning' : ''}>
                A linha cinza ao lado do assunto na caixa de entrada · {preheaderTamanho}/{LIMITE_PREHEADER} caracteres.
              </span>
            }
          >
            {({ id, describedBy }) => (
              <Input id={id} aria-describedby={describedBy} value={form.preheader} maxLength={140} placeholder="Ex.: Está tudo salvo do jeito que você deixou" onChange={(e) => alterar({ preheader: e.target.value })} />
            )}
          </Field>
          <Field label="Corpo" required hint={`Variáveis: ${VARIAVEIS_DO_EMAIL.join(' ')}. Uma linha em branco separa parágrafos.`}>
            {({ id, describedBy }) => <Textarea id={id} aria-describedby={describedBy} rows={7} value={form.body} placeholder="Oi, {primeiro_nome}!" onChange={(e) => alterar({ body: e.target.value })} />}
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Texto do botão" hint="Opcional. Sem texto, o e-mail sai sem botão.">
              {({ id, describedBy }) => <Input id={id} aria-describedby={describedBy} value={form.ctaLabel} maxLength={40} placeholder="Ex.: Concluir compra" onChange={(e) => alterar({ ctaLabel: e.target.value })} />}
            </Field>
            <Field label="URL do botão" required={form.ctaLabel.trim() !== ''}>
              {({ id }) => <Input id={id} type="url" inputMode="url" value={form.ctaUrl} placeholder="https://" onChange={(e) => alterar({ ctaUrl: e.target.value })} disabled={form.ctaLabel.trim() === ''} />}
            </Field>
          </div>
          <Field
            label="Público"
            required
            hint={
              publico > 0
                ? `≈ ${formatCount(publico)} destinatários agora, sem os descadastrados. Só públicos que já passaram pelo fato.`
                : 'Ninguém neste público nesta empresa agora (ou ela não tem esse funil ligado). Escolha outro público.'
            }
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={form.segmentKey}
                onChange={(e) => {
                  if (isSegmentKey(e.target.value)) {
                    alterar({ segmentKey: e.target.value });
                  }
                }}
              >
                {SEGMENT_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {SEGMENT_LABEL[k]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Checkbox checked={form.agendar} onChange={(v) => alterar({ agendar: v })} label="Agendar o envio" description="Sem agendar, o disparo começa na hora." />
          {form.agendar ? (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Data" required>
                {({ id }) => <Input id={id} type="date" min={toDateOnly(new Date())} value={form.dataAgendamento} onChange={(e) => alterar({ dataAgendamento: e.target.value })} />}
              </Field>
              <Field label="Hora" required>
                {({ id }) => <Input id={id} type="time" value={form.horaAgendamento} onChange={(e) => alterar({ horaAgendamento: e.target.value })} />}
              </Field>
            </div>
          ) : null}
          {completo ? null : <p className="text-xs text-ink-subtle">Para disparar: {pendencias.join(' · ')}.</p>}
        </div>

        <div className="flex flex-col gap-5 lg:sticky lg:top-0 lg:self-start">
          <PreviaEmailDispositivo
            from={remetente}
            subject={form.subject.trim() === '' ? 'Assunto do e-mail' : form.subject}
            preheader={form.preheader.trim() === '' ? undefined : form.preheader}
            body={form.body.trim() === '' ? 'O corpo do seu e-mail aparece aqui, com as variáveis destacadas.' : form.body}
            cta={form.ctaLabel.trim() === '' ? undefined : form.ctaLabel}
          />
          <div className="flex flex-col gap-2">
            <p className="text-sm font-medium text-ink">Teste de entrega</p>
            <ChecklistDeEntrega resultado={entrega} compacto />
          </div>
        </div>
      </div>
    </Modal>
  );
}
